import * as THREE from 'three';
import { getSkinColor, getAreolaColor } from './bustGeometry';

export const vertexShader = `
varying vec3 vWorldPos;
varying vec3 vWorldNormal;
varying vec2 vUv;

void main() {
  vUv = uv;
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorldPos = wp.xyz;
  vWorldNormal = normalize(mat3(modelMatrix) * normal);
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

export const fragmentShader = `
precision highp float;

uniform vec3 uSkinColor;
uniform vec3 uAreolaColor;
uniform float uAreolaRadius;
uniform float uRoughness;
uniform float uSssStrength;
uniform vec3 uCameraPos;

uniform float uVeins;

uniform vec3 uKeyPos;
uniform vec3 uKeyColor;
uniform float uKeyInt;
uniform vec3 uFillPos;
uniform vec3 uFillColor;
uniform float uFillInt;
uniform vec3 uRimPos;
uniform vec3 uRimColor;
uniform float uRimInt;
uniform vec3 uBotPos;
uniform vec3 uBotColor;
uniform float uBotInt;

varying vec3 vWorldPos;
varying vec3 vWorldNormal;
varying vec2 vUv;

vec3 toLinear(vec3 c) { return pow(max(c, vec3(0.001)), vec3(2.2)); }
vec3 toSRGB(vec3 c) { return pow(max(c, vec3(0.0)), vec3(1.0/2.2)); }

// Simple hashing for noise
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
float noise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f*f*(3.0-2.0*f);
  return mix(mix(hash(i + vec2(0,0)), hash(i + vec2(1,0)), u.x),
             mix(hash(i + vec2(0,1)), hash(i + vec2(1,1)), u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0; float a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise(p); p *= 2.0; a *= 0.5; }
  return v;
}

float phong(vec3 N, vec3 L, vec3 V, float rough) {
  vec3 H = normalize(L + V);
  return pow(max(0.0, dot(N, H)), mix(128.0, 4.0, rough));
}

void main() {
  vec3 N = normalize(vWorldNormal);
  vec3 V = normalize(uCameraPos - vWorldPos);

  // === BASE BLEND ===
  vec2 ac1 = vec2(0.0, 0.5);
  vec2 ac2 = vec2(1.0, 0.5);
  float d1 = length((vUv - ac1) * vec2(2.0, 1.0));
  float d2 = length((vUv - ac2) * vec2(2.0, 1.0));
  float aDist = min(d1, d2);
  // Even wider smoothstep for a much smoother transition (fade) into the areola (no sharp lines)
  float aFactor = 1.0 - smoothstep(uAreolaRadius * 0.45, uAreolaRadius + 0.12, aDist);

  vec3 skinLin = toLinear(uSkinColor);
  vec3 areoLin = toLinear(uAreolaColor);
  vec3 baseColor = mix(skinLin, areoLin, aFactor * 0.9);

  // Make the areola/nipple area noticeably more matte/rough (less glossy)
  // Simply take a base max roughness and blend transparency via aFactor
  float localRoughness = mix(uRoughness, clamp(uRoughness + 0.35, 0.2, 1.0), aFactor);

  // === DETAILS ===
  // Veins: networked branching lines using ridged noise
  if (uVeins > 0.01) {
    // Warp UVs for organic look
    vec2 vWarp = vUv * 12.0;
    vWarp.x += fbm(vUv * 4.0) * 0.15;
    vWarp.y += fbm(vUv * 4.0 + 1.5) * 0.15;
    
    // Multi-layered ridged noise
    float n1 = 1.0 - abs(noise(vWarp) * 2.0 - 1.0);
    float n2 = 1.0 - abs(noise(vWarp * 2.1 + 0.8) * 2.0 - 1.0);
    float vPattern = n1 * n2;
    
    // Mask down to thin lines
    float veinMask = smoothstep(0.72, 0.95, vPattern);
    vec3 veinCol = vec3(0.08, 0.14, 0.35) * uVeins;
    baseColor = mix(baseColor, veinCol, veinMask * uVeins * 0.3 * (1.0 - aFactor));
  }

  // === LIGHTING ===
  vec3 col = vec3(0.0);

  // Key light (warm, upper-front)
  vec3 L1 = normalize(uKeyPos - vWorldPos);
  float NL1 = dot(N, L1);
  float wrap = 0.3;
  float diff1 = max(0.0, (NL1 + wrap) / (1.0 + wrap));
  float spec1 = phong(N, L1, V, localRoughness) * 0.45 * (1.0 - localRoughness * 0.75);
  col += baseColor * diff1 * toLinear(uKeyColor) * uKeyInt;
  col += vec3(1.0, 0.97, 0.94) * spec1 * uKeyInt;

  // SSS: warm orange-red glow from backlit scatter
  float sssAmt = max(0.0, -NL1 + 0.25) * uSssStrength;
  vec3 sssColor = baseColor * vec3(1.35, 0.4, 0.18) * sssAmt * uKeyInt * 0.35;
  col += sssColor;

  // Fill light (cool blue)
  vec3 L2 = normalize(uFillPos - vWorldPos);
  float diff2 = max(0.0, dot(N, L2));
  col += baseColor * diff2 * toLinear(uFillColor) * uFillInt;

  // Rim / back light (neon cyan)
  vec3 L3 = normalize(uRimPos - vWorldPos);
  float rimFresnel = pow(1.0 - max(0.0, dot(N, V)), 2.5);
  float rimDiff = max(0.0, dot(N, L3));
  col += toLinear(uRimColor) * rimFresnel * rimDiff * uRimInt * 0.8;

  // Bottom fill (warm)
  vec3 L4 = normalize(uBotPos - vWorldPos);
  float diff4 = max(0.0, dot(N, L4));
  col += baseColor * diff4 * toLinear(uBotColor) * uBotInt;

  // Ambient
  col += baseColor * 0.14 * vec3(0.75, 0.82, 1.0);

  // Filmic tone map + gamma
  col = col / (col + vec3(0.85));
  col = toSRGB(col);

  gl_FragColor = vec4(col, 1.0);
}
`;

export function createSkinMaterial(params) {
  const skinRGB = getSkinColor(params.skinTone);
  const areoRGB = getAreolaColor(params.skinTone);
  const areolUV = 0.066 + params.areolRadius * 0.07;

  return new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uSkinColor:    { value: new THREE.Color(...skinRGB) },
      uAreolaColor:  { value: new THREE.Color(...areoRGB) },
      uAreolaRadius: { value: areolUV },
      uRoughness:    { value: params.roughness },
      uSssStrength:  { value: params.sssStrength },
      uVeins:        { value: params.veins },
      uCameraPos:    { value: new THREE.Vector3(0, 0, 5) },
      // Key
      uKeyPos:   { value: new THREE.Vector3(-2.5, 4, 3.5) },
      uKeyColor: { value: new THREE.Color(1.0, 0.93, 0.88) },
      uKeyInt:   { value: 2.2 },
      // Fill
      uFillPos:   { value: new THREE.Vector3(3.5, 0.5, 2) },
      uFillColor: { value: new THREE.Color(0.45, 0.55, 0.9) },
      uFillInt:   { value: 1.0 },
      // Rim
      uRimPos:   { value: new THREE.Vector3(-1.5, -0.5, -4) },
      uRimColor: { value: new THREE.Color(0.0, 0.85, 1.0) },
      uRimInt:   { value: 1.6 },
      // Bottom
      uBotPos:   { value: new THREE.Vector3(0, -4, 1.5) },
      uBotColor: { value: new THREE.Color(0.9, 0.7, 0.6) },
      uBotInt:   { value: 0.4 },
    },
    side: THREE.FrontSide,
  });
}
